import { z } from 'zod';
import {
  GenerationModeSchema,
  ModelFamilySchema,
  MeshyModelSchema,
  HunyuanModelSchema,
  ExportFormatSchema,
  ThreeDGenerationParamsSchema,
} from './3d-generation-params.schema';

/**
 * 3D Node State Schema
 * Defines the complete state structure for 3D model generation nodes
 *
 * This extends the base GenieNodeState with 3D-specific fields
 */

// ============================================================================
// OUTPUT SCHEMAS
// ============================================================================

/**
 * Generated 3D model file
 */
export const ThreeDModelFileSchema = z.object({
  url: z.string().url(),
  file_name: z.string(),
  file_size: z.number(),
  content_type: z.string(),
  format: ExportFormatSchema,
});

/**
 * Texture file (for PBR materials)
 */
export const TextureFileSchema = z.object({
  url: z.string().url(),
  file_name: z.string(),
  type: z.enum(['base_color', 'metallic', 'roughness', 'normal']),
});

/**
 * 3D Generation output (API response)
 */
export const ThreeDGenerationOutputSchema = z.object({
  // Model files (multiple formats)
  model_glb: ThreeDModelFileSchema.optional(),
  model_fbx: ThreeDModelFileSchema.optional(),
  model_usdz: ThreeDModelFileSchema.optional(),
  model_obj: ThreeDModelFileSchema.optional(),

  // Texture files (if PBR enabled)
  textures: z.array(TextureFileSchema).optional(),

  // Preview thumbnail
  thumbnail: z.string().url().optional(), // URL to preview image

  // Generation metadata
  seed: z.number().optional(),
  generation_time: z.number().optional(), // seconds
  cost: z.number().optional(), // USD
});

// ============================================================================
// NODE STATE SCHEMA
// ============================================================================

/**
 * Complete 3D Node State
 * Persisted to Magic Canvas and used for all 3D node operations
 */
export const ThreeDNodeStateSchema = z.object({
  // ===== CONFIGURATION =====

  /**
   * Generation mode (determines available models and parameters)
   */
  generationMode: GenerationModeSchema.default('image-to-3d'),

  /**
   * Selected model family
   */
  modelFamily: ModelFamilySchema.default('meshy'),

  /**
   * Selected model variant (depends on family)
   */
  meshyModel: MeshyModelSchema.optional(),
  hunyuanModel: HunyuanModelSchema.optional(),

  /**
   * Export formats to generate
   */
  exportFormats: z.array(ExportFormatSchema).default(['glb']),

  // ===== GENERATION PARAMETERS =====

  /**
   * Model-specific generation parameters
   * Type depends on selected model family and variant
   */
  generationParams: ThreeDGenerationParamsSchema.optional(),

  // ===== INPUT DATA =====

  /**
   * Text prompt (for text-to-3d mode)
   */
  prompt: z.string().optional(),

  /**
   * Single image input (for image-to-3d mode)
   * URL string on server, File object on client
   */
  image: z.union([z.string(), z.unknown()]).optional(),

  /**
   * Multiple images (for multi-image-to-3d mode)
   * - Meshy: 1-4 images from any angles
   * - Hunyuan multi-view: front, back, left (in order)
   */
  images: z.array(z.union([z.string(), z.unknown()])).optional(),

  /**
   * World generation labels (for image-to-world mode)
   */
  worldLabels: z.object({
    foreground1: z.string().optional(),
    foreground2: z.string().optional(),
    classes: z.string().optional(),
  }).optional(),

  // ===== OUTPUT DATA =====

  /**
   * Generated 3D model output
   */
  output: ThreeDGenerationOutputSchema.optional(),

  // ===== UI STATE =====

  /**
   * Node title (custom or auto-generated)
   */
  title: z.string().optional(),

  /**
   * Canvas managed states
   */
  selected: z.boolean().optional(),
  collapsed: z.boolean().optional(),
  locked: z.boolean().optional(),

  /**
   * Preview state
   */
  showPreview: z.boolean().default(true),
  previewMode: z.enum(['thumbnail', '3d-viewer']).default('thumbnail'),

  // ===== GENERATION STATE =====

  /**
   * Current generation status
   */
  status: z.enum(['idle', 'configuring', 'generating', 'completed', 'error']).default('idle'),

  /**
   * Error message (if status === 'error')
   */
  errorMessage: z.string().optional(),

  /**
   * Generation progress (0-100)
   */
  progress: z.number().min(0).max(100).optional(),

  /**
   * Task ID for async generation tracking
   */
  taskId: z.string().optional(),

  /**
   * Generation started timestamp
   */
  startedAt: z.date().optional(),

  /**
   * Generation completed timestamp
   */
  completedAt: z.date().optional(),

  // ===== COST & ESTIMATES =====

  /**
   * Estimated cost (calculated before generation)
   */
  estimatedCost: z.number().optional(),

  /**
   * Estimated generation time (seconds)
   */
  estimatedTime: z.number().optional(),

  /**
   * Actual cost (from API response)
   */
  actualCost: z.number().optional(),
  // ===== EXTENSIBILITY =====

  /**
   * Additional metadata for future extensions
   */
  metadata: z.record(z.string(), z.unknown()).optional(),
});

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * 3D node validation result
 */
export const ThreeDValidationResultSchema = z.object({
  valid: z.boolean(),
  errors: z.array(z.object({
    field: z.string(),
    message: z.string(),
  })),
});

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type ThreeDModelFile = z.infer<typeof ThreeDModelFileSchema>;
export type TextureFile = z.infer<typeof TextureFileSchema>;
export type ThreeDGenerationOutput = z.infer<typeof ThreeDGenerationOutputSchema>;
export type ThreeDNodeState = z.infer<typeof ThreeDNodeStateSchema>;
export type ThreeDValidationResult = z.infer<typeof ThreeDValidationResultSchema>;

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Default 3D node state
 */
export const DEFAULT_3D_NODE_STATE: Partial<ThreeDNodeState> = {
  generationMode: 'image-to-3d',
  modelFamily: 'hunyuan',
  hunyuanModel: 'v2-turbo',
  exportFormats: ['glb'],
  status: 'idle',
  showPreview: true,
  previewMode: 'thumbnail',
};
